/**
 * useExamSession
 * ──────────────
 * Manages a complete exam attempt lifecycle:
 *   loading → active → finished
 *
 * Usage:
 *   const session = useExamSession(examId)
 *
 * Returns:
 *   phase          : 'loading' | 'active' | 'finished' | 'error'
 *   error          : string | null
 *   exam           : object (exam detail)
 *   resultMeta     : { result_id, start_time, time_limit_minutes }
 *   finishData     : result after submission
 *   questions      : array of question objects
 *   totalQ         : number
 *   currentQ       : current question object
 *   currentIdx     : 0-based index
 *   answers        : { [questionId]: value }
 *   saveStatus     : { [questionId]: 'saving' | 'saved' | 'error' }
 *   saveAnswer     : (questionId, value) => void
 *   goNext         : () => void
 *   goPrev         : () => void
 *   goToQuestion   : (idx) => void
 *   finish         : () => void
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { getExam, startExam, saveAnswer as apiSaveAnswer, finishExam } from '@/api/exams'

export function useExamSession(examId) {
  const [phase,      setPhase]      = useState('loading')
  const [error,      setError]      = useState(null)
  const [exam,       setExam]       = useState(null)
  const [resultMeta, setResultMeta] = useState(null)
  const [finishData, setFinishData] = useState(null)
  const [questions,  setQuestions]  = useState([])
  const [currentIdx, setCurrentIdx] = useState(0)
  const [answers,    setAnswers]    = useState({})
  const [saveStatus, setSaveStatus] = useState({})

  // Prevent double-initialisation in StrictMode
  const initialised = useRef(false)
  // Track in-flight save promises so finish() can wait for them
  const pendingSaves = useRef(new Set())

  useEffect(() => {
    if (!examId || initialised.current) return
    initialised.current = true

    async function init() {
      try {
        // 1. Fetch exam detail
        const { data: examData } = await getExam(examId)
        setExam(examData)

        // 2. Start (or resume) attempt
        const { data: startData } = await startExam(examId)
        setResultMeta({
          result_id:          startData.result_id,
          start_time:         startData.start_time,
          time_limit_minutes: startData.time_limit_minutes,
        })

        // 3. Populate questions from snapshot
        const qs = startData.questions ?? []
        setQuestions(qs)

        // 4. Restore any already-saved answers
        const savedAnswers = {}
        const savedData    = startData.answers ?? {}
        Object.entries(savedData).forEach(([qid, entry]) => {
          savedAnswers[Number(qid)] = entry.answer
        })
        setAnswers(savedAnswers)

        setPhase('active')
      } catch (err) {
        // Attempts exhausted isn't an error for the user — they've already
        // completed the exam, so send them to their result instead.
        if (err.response?.data?.attempts_exhausted) {
          setPhase('completed')
          return
        }
        const msg =
          err.response?.data?.detail ??
          err.response?.data?.non_field_errors?.[0] ??
          err.message ??
          'Unknown error'
        setError(msg)
        setPhase('error')
      }
    }

    init()
  }, [examId])

  // ── Navigation ────────────────────────────────────────────────────
  const goNext = useCallback(() => {
    setCurrentIdx(i => Math.min(i + 1, questions.length - 1))
  }, [questions.length])

  const goPrev = useCallback(() => {
    setCurrentIdx(i => Math.max(i - 1, 0))
  }, [])

  const goToQuestion = useCallback((idx) => {
    setCurrentIdx(idx)
  }, [])

  // ── Save answer ───────────────────────────────────────────────────
  const saveAnswer = useCallback(async (questionId, value) => {
    // Optimistic local update
    setAnswers(prev => ({ ...prev, [questionId]: value }))
    setSaveStatus(prev => ({ ...prev, [questionId]: 'saving' }))
    const promise = apiSaveAnswer(examId, questionId, value)
    pendingSaves.current.add(promise)
    try {
      await promise
      setSaveStatus(prev => ({ ...prev, [questionId]: 'saved' }))
    } catch {
      setSaveStatus(prev => ({ ...prev, [questionId]: 'error' }))
    } finally {
      pendingSaves.current.delete(promise)
    }
  }, [examId])

  // ── Finish ────────────────────────────────────────────────────────
  const finish = useCallback(async () => {
    try {
      // Wait for any in-flight save-answer requests before finishing,
      // so the backend has the latest answers (especially for short-text / essay).
      if (pendingSaves.current.size > 0) {
        await Promise.allSettled([...pendingSaves.current])
      }
      const { data } = await finishExam(examId)
      setFinishData(data)
      setPhase('finished')
    } catch (err) {
      const msg = err.response?.data?.detail ?? err.message
      setError(msg)
      setPhase('error')
    }
  }, [examId])

  const currentQ = questions[currentIdx] ?? null
  const totalQ   = questions.length

  return {
    phase,
    error,
    exam,
    resultMeta,
    finishData,
    questions,
    totalQ,
    currentQ,
    currentIdx,
    answers,
    saveStatus,
    saveAnswer,
    goNext,
    goPrev,
    goToQuestion,
    finish,
  }
}

export default useExamSession

